<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <h3>Sync Gateway-brukere</h3>
    <div class="mt-2" style="font-style: italic; color:#666">
      <span>Alle merchant-brukere registrert i Sync Gateway. Hver bruker har tilgang til sin
        tenant-kanal (merchant-slug), som POS/KIOSK-enhetene replikerer.</span>
    </div>

    <div class="mt-3 mb-3 d-flex align-items-end flex-wrap" style="gap:1rem">
      <div>
        <label class="form-label mb-1" style="font-size:0.85rem; color:#666">Søk</label>
        <input class="form-control" style="max-width:340px" type="text" v-model="search"
               placeholder="Brukernavn eller kanal…" />
      </div>
      <div>
        <button class="btn btn-outline-secondary" @click="search = ''" :disabled="!search">Nullstill</button>
      </div>
      <div class="ms-auto" style="color:#666; padding-bottom:0.4rem">{{ filteredUsers.length }} av {{ users.length }}</div>
    </div>

    <p v-if="error" class="text-danger">{{ error }}</p>

    <div v-if="!loading && !error && users.length === 0" style="color:#666">
      Ingen Sync Gateway-brukere funnet.
    </div>

    <table v-else-if="!error" class="table table-hover align-middle">
      <thead>
        <tr>
          <th>Brukernavn</th>
          <th>Kanaler</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="u in filteredUsers" :key="u.name">
          <td><code>{{ u.name }}</code></td>
          <td>
            <span v-if="u.adminChannels && u.adminChannels.length">
              <code v-for="ch in u.adminChannels" :key="ch" class="me-2">{{ ch }}</code>
            </span>
            <span v-else style="color:#999">—</span>
          </td>
          <td>
            <span v-if="u.disabled" class="badge bg-secondary">Deaktivert</span>
            <span v-else class="badge bg-success">Aktiv</span>
          </td>
          <td class="text-end">
            <button class="btn btn-sm btn-outline-danger" title="Slett Sync Gateway-bruker" @click="openDelete(u)">
              Slett
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <popup-modal ref="deleteModal">
    <h3>Slett Sync Gateway-bruker</h3>
    <div class="mt-3 mb-2" v-if="deleteTarget">
      Dette fjerner Sync Gateway-brukeren <strong>{{ deleteTarget.name }}</strong>. Merchantens
      POS/KIOSK-enheter kan <em>ikke</em> lenger synkronisere før brukeren opprettes på nytt.
      Handlingen kan ikke angres.
    </div>
    <div class="mb-2" style="color:#666" v-if="deleteTarget">
      Skriv inn brukernavnet <strong>{{ deleteTarget.name }}</strong> for å bekrefte:
    </div>
    <input class="form-control" type="text" v-model="deleteConfirmText"
           :placeholder="deleteTarget ? deleteTarget.name : ''" @keyup.enter="confirmDelete" />
    <p v-if="deleteError" class="text-danger mt-2 mb-0">{{ deleteError }}</p>
    <div class="mt-3" style="display:flex; justify-content:flex-end; gap:1rem; border-top:1px solid #ccc; padding-top:15px">
      <button class="btn btn-outline-secondary" @click="cancelDelete" :disabled="deleting">Avbryt</button>
      <button class="btn btn-danger" @click="confirmDelete" :disabled="deleting || !deleteConfirmMatches">
        {{ deleting ? 'Sletter…' : 'Slett' }}
      </button>
    </div>
  </popup-modal>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import PopupModal from "@/components/other/PopupModal";
import SystemService from "@/services/system/SystemService";

export default {
  name: "SyncGatewayUsers",
  components: { LoadingSpinner, PopupModal },
  data() {
    return {
      loading: true,
      users: [],
      search: "",
      error: "",
      deleteTarget: null,
      deleteConfirmText: "",
      deleting: false,
      deleteError: ""
    };
  },
  computed: {
    filteredUsers() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter((u) => {
        const channels = Array.isArray(u.adminChannels) ? u.adminChannels.join(" ") : "";
        return [u.name, channels]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
    },
    deleteConfirmMatches() {
      if (!this.deleteTarget) return false;
      const typed = this.deleteConfirmText.trim().toLowerCase();
      const name = (this.deleteTarget.name || "").trim().toLowerCase();
      return typed.length > 0 && typed === name;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      SystemService.listSyncGatewayUsers().then(
        (response) => {
          this.users = Array.isArray(response.data) ? response.data : [];
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.error = "Kunne ikke hente Sync Gateway-brukere.";
          this.loading = false;
        }
      );
    },
    openDelete(u) {
      this.deleteTarget = u;
      this.deleteConfirmText = "";
      this.deleteError = "";
      this.$refs.deleteModal.open();
    },
    cancelDelete() {
      this.$refs.deleteModal.close();
      this.deleteTarget = null;
      this.deleteConfirmText = "";
      this.deleteError = "";
    },
    confirmDelete() {
      if (!this.deleteConfirmMatches || this.deleting) return;
      const target = this.deleteTarget;
      this.deleting = true;
      this.deleteError = "";
      SystemService.deleteSyncGatewayUser(target.name).then(
        () => {
          this.users = this.users.filter((x) => x.name !== target.name);
          this.deleting = false;
          this.$refs.deleteModal.close();
          this.deleteTarget = null;
          this.deleteConfirmText = "";
        },
        (error) => {
          this.deleting = false;
          const data = error && error.response ? error.response.data : null;
          this.deleteError = (data && data.error) || "Kunne ikke slette. Prøv igjen.";
        }
      );
    }
  }
};
</script>
